import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faAngleLeft,
  faAngleRight,
  faCopy,
  faMagnifyingGlass,
  faSquarePlus,
  faSquareXmark
} from '@fortawesome/free-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { validateAllFormFields } from '../../../common/utils';
import { ApiService } from '../../services/api.service';
import {CopyButtonComponent} from '../../shared/copy-button/copy-button.component';
import {DEFAULT_TABLE_PAGE_SIZE_OPTIONS} from '../../shared/constants/pagination.constants';
import {VtePaginationComponent} from '../../shared/vte/vte-pagination/vte-pagination.component';

type WebhookApiKeyRow = {
  id: number;
  key_prefix: string;
  provider: number;
  provider_name: string;
  organization: number;
  organization_name: string;
  is_active: boolean;
  created_at: string;
  _toggling?: boolean;
};

type SelectOption = {
  id: number;
  label: string;
};

@Component({
    selector: 'app-api-keys',
    imports: [CommonModule, FormsModule, ReactiveFormsModule, FontAwesomeModule, CopyButtonComponent, VtePaginationComponent],
    templateUrl: './api-keys.component.html',
    styleUrl: './api-keys.component.css'
})
export class ApiKeysComponent {
    readonly pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;
  apiKeys: WebhookApiKeyRow[] = [];
  providers: SelectOption[] = [];
  organizations: SelectOption[] = [];
  apiKeyForm!: FormGroup;

  searchText = '';
  providerFilter = '';
  organizationFilter = '';
  recordsPerPage = 10;
  currentPage = 1;
  totalRecords = 0;
  nextPage: string | null = null;
  previousPage: string | null = null;
  loading = false;
  saving = false;
  loadingOptions = false;
  showGenerateModal = false;
  showSuccessModal = false;
  generatedRawKey = '';

  constructor(
    private apiService: ApiService,
    private toastr: ToastrService,
    private fb: FormBuilder,
    library: FaIconLibrary
  ) {
    library.addIcons(
      faSquarePlus,
      faMagnifyingGlass,
      faAngleLeft,
      faAngleRight,
      faSquareXmark,
      faCopy
    );
  }

  ngOnInit() {
    this.apiKeyForm = this.fb.group({
      provider: ['', Validators.required],
      organization: ['', Validators.required],
      is_active: [true]
    });
    this.loadSelectOptions();
    this.fetchApiKeys();
  }

  get totalPages(): number {
    const totalPages = Math.ceil(this.totalRecords / this.recordsPerPage);
    return totalPages === 0 ? 1 : totalPages;
  }

  fetchApiKeys() {
    this.loading = true;
    this.apiService
      .getWebhookApiKeys(
        this.currentPage,
        this.recordsPerPage,
        this.searchText,
        this.providerFilter || null,
        this.organizationFilter || null
      )
      .subscribe({
        next: (response: any) => {
          this.apiKeys = response?.results ?? [];
          this.totalRecords = response?.count ?? 0;
          this.nextPage = response?.next ?? null;
          this.previousPage = response?.previous ?? null;
          this.loading = false;
        },
        error: (err) => {
          this.apiKeys = [];
          this.totalRecords = 0;
          this.nextPage = null;
          this.previousPage = null;
          this.loading = false;
          if (err?.error?.detail) {
            this.toastr.error(err.error.detail, 'Error');
          } else if (err?.status !== 403 && err?.status !== 0) {
            this.toastr.error('Unable to load API keys.', 'Error');
          }
        }
      });
  }

  loadSelectOptions() {
    this.loadingOptions = true;

    this.apiService.getWebhookProviders(1, 1000, '').subscribe({
      next: (response: any) => {
        this.providers = (response?.results ?? []).map((provider: any) => ({
          id: provider.id,
          label: provider.display_name || provider.name
        }));
        this.loadingOptions = false;
      },
      error: () => {
        this.providers = [];
        this.loadingOptions = false;
      }
    });

    this.apiService.getOrganizations(1000).subscribe({
      next: (response: any) => {
        this.organizations = (response?.results ?? []).map((organization: any) => ({
          id: organization.id,
          label: organization.org_name || organization.name
        }));
      },
      error: () => {
        this.organizations = [];
      }
    });
  }

  onSearch() {
    this.currentPage = 1;
    this.fetchApiKeys();
  }

  resetFilters() {
    if (!this.searchText && !this.providerFilter && !this.organizationFilter) {
      return;
    }
    this.searchText = '';
    this.providerFilter = '';
    this.organizationFilter = '';
    this.currentPage = 1;
    this.fetchApiKeys();
  }

  next() {
    if (!this.nextPage) {
      return;
    }
    this.currentPage += 1;
    this.fetchApiKeys();
  }

  previous() {
    if (!this.previousPage) {
      return;
    }
    this.currentPage -= 1;
    this.fetchApiKeys();
  }

  selectPageSize() {
    this.currentPage = 1;
    this.fetchApiKeys();
  }

    onPageChange(page: number): void {
        this.currentPage = page;
        this.fetchApiKeys();
    }

    onPageSizeChange(pageSize: number): void {
        this.recordsPerPage = pageSize;
        this.currentPage = 1;
        this.fetchApiKeys();
    }

  openGenerateModal() {
    this.apiKeyForm.reset({
      provider: '',
      organization: '',
      is_active: true
    });
    this.showGenerateModal = true;
  }

  closeGenerateModal() {
    this.showGenerateModal = false;
    this.saving = false;
  }

  submitGenerate() {
    if (this.apiKeyForm.invalid) {
      validateAllFormFields(this.apiKeyForm);
      return;
    }

    this.saving = true;
    this.apiService.createWebhookApiKey(this.apiKeyForm.value).subscribe({
      next: (response: any) => {
        this.saving = false;
        this.showGenerateModal = false;
        this.generatedRawKey = response?.raw_key ?? '';
        this.showSuccessModal = !!this.generatedRawKey;
        this.fetchApiKeys();
        this.toastr.success('API key generated successfully.', 'Success');
      },
      error: (err) => {
        this.saving = false;
        if (err?.error?.provider?.length) {
          err.error.provider.forEach((message: string) => this.toastr.error(message, 'Error'));
        } else if (err?.error?.organization?.length) {
          err.error.organization.forEach((message: string) => this.toastr.error(message, 'Error'));
        } else if (err?.error?.detail) {
          this.toastr.error(err.error.detail, 'Error');
        } else if (err?.status !== 403 && err?.status !== 0) {
          this.toastr.error('Unable to generate API key.', 'Error');
        }
      }
    });
  }

  closeSuccessModal() {
    this.generatedRawKey = '';
    this.showSuccessModal = false;
  }

  toggleApiKeyStatus(apiKey: WebhookApiKeyRow, event?: Event) {
    const target = event?.target as HTMLInputElement | undefined;
    const nextStatus = target ? target.checked : !apiKey.is_active;
    const previousStatus = apiKey.is_active;
    apiKey.is_active = nextStatus;
    apiKey._toggling = true;

    this.apiService.updateWebhookApiKey(apiKey.id, { is_active: nextStatus }).subscribe({
      next: () => {
        apiKey._toggling = false;
        this.toastr.success(
          `API key ${nextStatus ? 'enabled' : 'disabled'} successfully.`,
          'Success'
        );
      },
      error: (err) => {
        apiKey.is_active = previousStatus;
        apiKey._toggling = false;
        if (err?.error?.detail) {
          this.toastr.error(err.error.detail, 'Error');
        } else if (err?.status !== 403 && err?.status !== 0) {
          this.toastr.error('Something went wrong!', 'Error');
        }
      }
    });
  }

  maskedKeyPrefix(keyPrefix: string): string {
    return keyPrefix ? `${keyPrefix}****` : '****';
  }
}
